// Shared adaptive bandwidth measurement and Output Profile tier selection.
// Live playback also uses the already headroom-adjusted result to choose
// between the raw proxy path and an Output Profile transcode.

export const ADAPTIVE_SPEED_TEST_URL = '/proxy/ts/speedtest';
export const ADAPTIVE_HEADROOM_FACTOR = 0.90;
export const ADAPTIVE_FALLBACK_PROFILE = '2.5-3.5m';
export const ADAPTIVE_DIRECT_SAFE_MBPS = 25;

const ADAPTIVE_WARMUP_BYTES = 512 * 1024;
const ADAPTIVE_MEASURE_BYTES = 1536 * 1024;
const ADAPTIVE_TOTAL_BYTES =
  ADAPTIVE_WARMUP_BYTES + ADAPTIVE_MEASURE_BYTES;
const ADAPTIVE_TAIL_BYTES = 1 * 1024 * 1024;
const ADAPTIVE_TAIL_START_BYTES =
  ADAPTIVE_TOTAL_BYTES - ADAPTIVE_TAIL_BYTES;
const ADAPTIVE_SPEED_TEST_TIMEOUT_MS = 45 * 1000;

// Only duplicate React calls while one measurement is currently in flight are
// joined. The result is discarded immediately after completion: every new
// Live playback performs a fresh client-side speed test. Archive is direct and
// intentionally does not run this measurement.
let adaptiveMeasurementPromise = null;

// DISPATCHARR_UNIFIED_ADAPTIVE_DELIVERY_R8_23_0
// Tier selection is based on the profile MAXRATE, not its target bitrate.
// Therefore a selected VBR tier never intentionally exceeds the already
// headroom-adjusted (90%) client budget. The 2.5-3.5 tier remains the floor
// when the measured link is below 3.5 Mbps because no smaller profile exists.
export const chooseAdaptiveOutputProfile = (measuredMbps) => {
  const safeMbps = measuredMbps * ADAPTIVE_HEADROOM_FACTOR;

  if (safeMbps >= 20.5) return '19.5-20.5m';
  if (safeMbps >= 19.5) return '18.5-19.5m';
  if (safeMbps >= 18.5) return '17.5-18.5m';
  if (safeMbps >= 17.5) return '16.5-17.5m';
  if (safeMbps >= 16.5) return '15.5-16.5m';
  if (safeMbps >= 15.5) return '14.5-15.5m';
  if (safeMbps >= 14.5) return '13.5-14.5m';
  if (safeMbps >= 13.5) return '12.5-13.5m';
  if (safeMbps >= 12.5) return '11.5-12.5m';
  if (safeMbps >= 11.5) return '10.5-11.5m';
  if (safeMbps >= 10.5) return '9.5-10.5m';
  if (safeMbps >= 9.5) return '8.5-9.5m';
  if (safeMbps >= 8.5) return '7.5-8.5m';
  if (safeMbps >= 7.5) return '6.5-7.5m';
  if (safeMbps >= 6.5) return '5.5-6.5m';
  if (safeMbps >= 5.5) return '4.5-5.5m';
  if (safeMbps >= 4.5) return '3.5-4.5m';
  return ADAPTIVE_FALLBACK_PROFILE;
};

// safeMbps already includes ADAPTIVE_HEADROOM_FACTOR. Keep that explicit here
// so the 25 Mbps direct threshold never receives the 10% reserve twice.
export const chooseAdaptiveLiveDelivery = (safeMbps) =>
  Number.isFinite(safeMbps) && safeMbps >= ADAPTIVE_DIRECT_SAFE_MBPS
    ? 'direct'
    : 'transcode';

const interpolatedThresholdTime = (
  thresholdBytes,
  bytesBefore,
  bytesAfter,
  readStartedAt,
  readFinishedAt
) => {
  if (bytesAfter <= bytesBefore || thresholdBytes <= bytesBefore) {
    return readStartedAt;
  }

  const fraction = Math.min(
    1,
    Math.max(
      0,
      (thresholdBytes - bytesBefore) / (bytesAfter - bytesBefore)
    )
  );

  return (
    readStartedAt +
    (readFinishedAt - readStartedAt) * fraction
  );
};

const performAdaptiveOutputMeasurement = async () => {
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    ADAPTIVE_SPEED_TEST_TIMEOUT_MS
  );

  try {
    const response = await fetch(
      `${ADAPTIVE_SPEED_TEST_URL}?nonce=${Date.now()}`,
      {
        cache: 'no-store',
        credentials: 'same-origin',
        signal: controller.signal,
      }
    );

    if (!response.ok) {
      throw new Error(`Speed test returned HTTP ${response.status}`);
    }

    if (!response.body?.getReader) {
      throw new Error(
        'Streaming response reader is not supported by this browser'
      );
    }

    const reader = response.body.getReader();
    let receivedBytes = 0;
    let warmupFinishedAt = null;
    let tailStartedAt = null;
    let testFinishedAt = null;

    while (true) {
      const readStartedAt = performance.now();
      const { value, done } = await reader.read();
      const readFinishedAt = performance.now();

      if (done) {
        testFinishedAt = readFinishedAt;
        break;
      }

      const bytesBefore = receivedBytes;
      receivedBytes += value?.byteLength || 0;

      if (
        warmupFinishedAt === null &&
        receivedBytes >= ADAPTIVE_WARMUP_BYTES
      ) {
        warmupFinishedAt = interpolatedThresholdTime(
          ADAPTIVE_WARMUP_BYTES,
          bytesBefore,
          receivedBytes,
          readStartedAt,
          readFinishedAt
        );
      }

      if (
        tailStartedAt === null &&
        receivedBytes >= ADAPTIVE_TAIL_START_BYTES
      ) {
        tailStartedAt = interpolatedThresholdTime(
          ADAPTIVE_TAIL_START_BYTES,
          bytesBefore,
          receivedBytes,
          readStartedAt,
          readFinishedAt
        );
      }
    }

    if (receivedBytes < ADAPTIVE_TOTAL_BYTES) {
      throw new Error(
        `Incomplete speed test: ${receivedBytes} of ` +
          `${ADAPTIVE_TOTAL_BYTES} bytes`
      );
    }

    if (
      warmupFinishedAt === null ||
      tailStartedAt === null ||
      testFinishedAt === null
    ) {
      throw new Error('Speed-test timing boundaries were not reached');
    }

    const sustainedSeconds = Math.max(
      (testFinishedAt - warmupFinishedAt) / 1000,
      0.05
    );
    const tailSeconds = Math.max(
      (testFinishedAt - tailStartedAt) / 1000,
      0.05
    );

    const sustainedMbps =
      (ADAPTIVE_MEASURE_BYTES * 8) /
      sustainedSeconds /
      1_000_000;
    const tailMbps =
      (ADAPTIVE_TAIL_BYTES * 8) /
      tailSeconds /
      1_000_000;
    const safeMbps = tailMbps * ADAPTIVE_HEADROOM_FACTOR;
    const profile = chooseAdaptiveOutputProfile(tailMbps);
    const delivery = chooseAdaptiveLiveDelivery(safeMbps);

    console.info(
      `[adaptive-output] no-cache test: ` +
        `warmup=0.5 MiB, measured=1.5 MiB, ` +
        `sustained=${sustainedMbps.toFixed(1)} Mbps, ` +
        `tail=${tailMbps.toFixed(1)} Mbps, ` +
        `safe=${safeMbps.toFixed(1)} Mbps -> ${delivery} ` +
        `(fallback profile ${profile})`
    );

    lastMeasuredSafeMbps =
      typeof safeMbps === 'number' && Number.isFinite(safeMbps)
        ? safeMbps
        : null;

    return {
      profile,
      delivery,
      measuredMbps: tailMbps,
      sustainedMbps,
      safeMbps,
    };
  } catch (error) {
    console.warn(
      `[adaptive-output] Speed test failed; using ${ADAPTIVE_FALLBACK_PROFILE}`,
      error
    );
    return {
      profile: ADAPTIVE_FALLBACK_PROFILE,
      delivery: 'transcode',
      measuredMbps: null,
      sustainedMbps: null,
      safeMbps: null,
    };
  } finally {
    window.clearTimeout(timeout);
  }
};

export const measureAdaptiveOutputProfile = async () => {
  if (!adaptiveMeasurementPromise) {
    adaptiveMeasurementPromise =
      performAdaptiveOutputMeasurement().finally(() => {
        adaptiveMeasurementPromise = null;
      });
  }

  return adaptiveMeasurementPromise;
};

// DISPATCHARR_WEB_LIVE_TUNING_PER_PROFILE_R34
// The live-reopen gate runs before the playback URL is built, so it has no
// profile of its own to send. Remember the last one attached here and let the
// gate reuse it; the first Watch Live of a session still falls back to the flat
// web_live values.
let lastAdaptiveOutputProfile = null;

// DISPATCHARR_WEB_LIVE_VIDEO_COPY_GATE_R42: the backend decides between an
// NVENC tier and a bit-exact c:v copy, and that needs the real measured number
// -- the tier key saturates at 19.5 Mbps and would make a 300 Mbps client look
// identical to a 20 Mbps one. safeMbps is the headroom-adjusted figure the
// profile itself was picked from.
let lastMeasuredSafeMbps = null;

// DISPATCHARR_WEB_LIVE_DIRECT_ADVERTISED_R49: the speed test alone cannot know
// whether the server will actually serve raw TS. When it will not, asking anyway
// costs a failed first attempt AND a second connection that joins the proxy ring
// buffer mid-GOP, which is where the browser's decode errors come from.
let serverDirectAllowed = true;

export const setServerDirectAllowed = (allowed) => {
  serverDirectAllowed = allowed !== false;
};

export const getLastAdaptiveOutputProfile = () => lastAdaptiveOutputProfile;

export const appendAdaptiveOutputProfile = (url, profile, delivery) => {
  if (!url || typeof url !== 'string') return url;
  const adaptiveUrl = new URL(url, window.location.origin);
  const resolvedProfile = profile || ADAPTIVE_FALLBACK_PROFILE;
  lastAdaptiveOutputProfile = resolvedProfile;
  adaptiveUrl.searchParams.set('adaptive_output_profile', resolvedProfile);
  if (lastMeasuredSafeMbps && lastMeasuredSafeMbps > 0) {
    adaptiveUrl.searchParams.set(
      'adaptive_client_mbps',
      lastMeasuredSafeMbps.toFixed(2)
    );
  }
  const effectiveDelivery =
    delivery === 'direct' && !serverDirectAllowed ? 'transcode' : delivery;
  if (effectiveDelivery === 'direct' || effectiveDelivery === 'transcode') {
    adaptiveUrl.searchParams.set('adaptive_delivery', effectiveDelivery);
  }
  adaptiveUrl.searchParams.delete('output_profile');
  return adaptiveUrl.toString();
};

export const withAdaptiveLiveTranscodeFallback = (url) => {
  if (
    !url ||
    typeof url !== 'string' ||
    !url.includes('/proxy/ts/stream/')
  ) {
    return url;
  }

  const adaptiveUrl = new URL(url, window.location.origin);
  adaptiveUrl.searchParams.set('adaptive_delivery', 'transcode');
  adaptiveUrl.searchParams.delete('adaptive_video_copy');
  // R42: this builder exists because playback already failed once. Veto the
  // video-copy gate so the retry cannot be handed the same choice again.
  adaptiveUrl.searchParams.set('adaptive_video_copy_gate', 'off');
  if (!adaptiveUrl.searchParams.get('adaptive_output_profile')) {
    adaptiveUrl.searchParams.set(
      'adaptive_output_profile',
      ADAPTIVE_FALLBACK_PROFILE
    );
  }
  adaptiveUrl.searchParams.delete('output_profile');
  return adaptiveUrl.toString();
};


export const directMseCodecsAreSupported = (mediaInfo) => {
  const videoCodec = String(mediaInfo?.videoCodec || '').toLowerCase();
  const audioCodec = String(mediaInfo?.audioCodec || '').toLowerCase();

  return videoCodec.startsWith('avc1.') && audioCodec === 'mp4a.40.2';
};

export const withAdaptiveLiveOutputProfile = async (url) => {
  if (
    !url ||
    typeof url !== 'string' ||
    !url.includes('/proxy/ts/stream/')
  ) {
    return url;
  }

  const result = await measureAdaptiveOutputProfile();
  return appendAdaptiveOutputProfile(
    url,
    result.profile,
    result.delivery
  );
};

// DISPATCHARR_UNIFIED_ADAPTIVE_DELIVERY_R8_23_0
// Compatibility with newer App.jsx snapshots that still call the old
// app-start prewarm hook. Live playback deliberately measures fresh
// bandwidth on each start, therefore this hook performs no network I/O.
export const prewarmAdaptiveOutputProfile = () => Promise.resolve(null);
